import ApiService from "@/core/services/ApiService";
import JwtService from "@/core/services/JwtService";
import router from "@/router";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";

export interface FacultyResponse {
  status: string;
  message: string;
  data: Faculty;
}

export interface Faculty {
  id: number;
  irbApplicationId: number;
  irbApplication: string;
  userId: string;
  user: string;
  sponsorDegree: string;
  email: string;
  signature: string;
  fullname: string;
  reseachDescription: string;
  address: string;
  createdDate: Date;
  updatedDate: Date;
  isApproved: boolean;
  decisionDate: Date;
}

export interface NonStandardResponse {
  status: string;
  message: string;
  data: NonStandard;
}

export interface NonStandard {
  irbApplicationId: number;
  category: string;
  reason: string;
}

export interface ResearchResponse {
  status: string;
  message: string;
  data: Research;
}

export interface SubjectResponse {
  status: string;
  message: string;
  data: Subject;
}

export interface RecruitmentResponse {
  status: string;
  message: string;
  data: Recruitment;
}
export interface RiskResponse {
  status: string;
  message: string;
  data: Subject;
}

export interface InformedResponse {
  status: string;
  message: string;
  data: Subject;
}

export interface DataResponse {
  status: string;
  message: string;
  data: Subject;
}

export interface Recruitment {
  irbApplicationId: number;
  strategy: string;
  areChosenFromRecord: boolean;
  approvedPerson: string;
  hasIdentifiableInformation: boolean;
  identifiableInformation: string;
  hasAccess: boolean;
  willAccessFromRecord: boolean;
  accessType: string;
  accessTypeOther: string;
  willSubjectsReceiveAnything: boolean;
  receiveDetail: string;
  createdDate: Date;
  updatedDate: Date;
}
export interface Research {
  irbApplicationId: number;
  researchProposals: Array<SubQuestionScreening>;
  fundingAgency: string;
  isApprovalRequired: boolean;
  startDate: Date;
  endDate: Date;
  purpose: string;
  method: string;
  createdDate: Date;
  updatedDate: Date;
}

export interface Subject {
  irbApplicationId: number;
  researchSubject: string;
  population: Array<SubQuestionScreening>;
  populationOther: string;
  safeGuard: string;
  numSubRecruited: number;
  subRecruitedTypes: Array<SubQuestionScreening>;
  subRecruitedOther: string;
  createdDate: Date;
  updatedDate: Date;
}

export interface Reviewer {
  status: string;
  description: string;
  assignedDate: Date;
  signature: string;
  decisionDate: Date;
}

export interface ReviewNote {
  type: string;
  note: string;
  createdDate: Date;
  updatedDate: Date;
  reviewer: string;
}

export interface Vote {
  decision: string;
  reason: string;
  votedDate: Date;
  voterName: string;
}

export interface NotificationForm {
  irbApplicationId: number;
  applicationCode: string;
  irbTitle: string;
  submittedUser: string;
  type: string;
  remark: string;
  status: string;
  createdDate: Date;
  assigned: string;
  decisionDate: Date;
}
export interface ApplicationResponse {
  status: string;
  message: string;
  data: Application;
}

export interface ApplicationResponse {
  status: string;
  message: string;
  data: Application;
}

export interface ReviewNoteResponse {
  status: string;
  message: string;
  data: ReviewNote;
}

export interface VoteResponse {
  status: string;
  message: string;
  data: Vote;
}

export interface Paging {
  page: number;
  perPage: number;
  pageCount: number;
  totalCount: number;
}

export interface Application {
  id: number;
  title: string;
  typeOfReview: string;
  applicationCode: string;
  appStatus: string;
  createdDate: Date;
  submittedDate: Date;
  irbStatus: string;
  irbDate: string;
  description: string;
}

export interface Screening {
  status: string;
  message: string;
  data: Question;
}

export interface Question {
  question1: boolean;
  question2: boolean;
  question3: boolean;
  question4: Array<SubQuestionScreening>;
}

export interface SubQuestionScreening {
  value: number;
  text: string;
  isChecked: boolean;
}

export interface CoInvestigator {
  id: number;
  irbApplicationId: number;
  email: string;
  title: string;
  firstname: string;
  lastname: string;
  createdDate: Date;
  updatedDate: Date;
  role: string;
}

export interface IrbMember {
  id: string;
  email: string;
  title: string;
  firstname: string;
  middlename: string;
  lastname: string;
  role: string;
}

export interface SearchByEmailResponse {
  status: string;
  message: string;
  data: CoInvestigator;
}

export interface CoInvestigatorResponse {
  status: string;
  message: string;
  data: CoInvestigator;
}

export interface DocumentData {
  id: number;
  irbApplicationId: number;
  type: string;
  name: string;
  url: string;
  createdDate: Date;
}

export interface DocumentResponse {
  status: string;
  message: string;
  data: DocumentData;
}

export interface DocumentsResponse {
  status: string;
  message: string;
  data: Array<DocumentData>;
}

export interface IrbchairAppsResponse {
  status: string;
  message: string;
  paging: Paging;
  filter: IrbChairFilter;
  data: Array<IrbchairApp>;
}

export interface ApprovalLetter {
  irbApplicationId: number;
  applicationCode: string;
  irbTitle: string;
  typeOfReview: string;
  submittedDate: Date;
  irbStatus: string;
  irbDate: Date;
  irbExpireDate: Date;
  reviewerEmail: string;
  reviewerTitle: string;
  reviewerFirstName: string;
  reviewerMiddleName: string;
  reviewerLastName: string;
  approvalLetter: string;
}

export interface IrbchairApp {
  irbApplicationId: number;
  applicationCode: string;
  irbTitle: string;
  typeOfReview: string;
  createdDate: Date;
  appStatus: string;
  submittedDate: Date;
  irbStatus: string;
  piEmail: string;
  piFirstname: string;
  piLastName: string;
  piUserType: string;
  reviewerId: string;
  reviewerEmail: string;
  reviewerTitle: string;
  reviewerFirstName: string;
  reviewerMiddleName: boolean;
  reviewerLastName: boolean;
  assignedDate: Date;
  reviewerStatus: string;
  reviewerDesc: string;
}

export interface IrbApplicationInfo {
  applications: ApplicationResponse;
  submittedApplications: Array<Application>;
  collaborativeApplications: Array<Application>;
  application: ApplicationResponse;
  faculty: FacultyResponse;
  research: ResearchResponse;
  risk: RiskResponse;
  informed: InformedResponse;
  data: DataResponse;
  screening: Screening;
  coInvestigators: Array<CoInvestigator>;
  error: string;
  searchByEmail: SearchByEmailResponse;
  coInvestigator: CoInvestigatorResponse;
  coInvestigatorEdit: CoInvestigator;
  documentResponse: DocumentResponse;
  documentsResponse: DocumentsResponse;
  irbchairAppsResponse: IrbchairAppsResponse;
  irbMembers: Array<IrbMember>;
  irbchairApp: IrbchairApp;
  irbMemberAssignedApps: IrbchairAppsResponse;
  irbMemberStandardApps: IrbchairAppsResponse;
  irbAppEdit: Application;
  nonStandard: NonStandardResponse;
  reviewer: Reviewer;
  reviewNotes: ReviewNoteResponse;
  reviewNoteEdit: ReviewNote;
  votes: VoteResponse;
  vote: Vote;
  approvalLetters: Array<ApprovalLetter>;
  notificationForms: Array<NotificationForm>;
  dashboardInfo;
}

export interface ApplicationsResponse {
  status: string;
  message: string;
  paging: Paging;
  data: Array<Application>;
}

export interface Paging {
  page: number;
  perPage: number;
  pageCount: number;
  totalCount: number;
}

export interface IrbChairFilter {
  title: string;
  typeOfReview: string;
  appCode: string;
  appStatus: string;
  irbStatus: string;
  reviewer: string;
}

@Module
export default class IrbApplicationModule
  extends VuexModule
  implements IrbApplicationInfo
{
  applications = {} as ApplicationResponse;
  submittedApplications = [];
  collaborativeApplications = [];
  application = {} as ApplicationResponse;
  faculty = {} as FacultyResponse;
  appInformationSearch = {} as ApplicationResponse;
  research = {} as ResearchResponse;
  subject = {} as SubjectResponse;
  recruitment = {} as RecruitmentResponse;
  risk = {} as RiskResponse;
  informed = {} as InformedResponse;
  data = {} as DataResponse;
  screening = {} as Screening;
  coInvestigators = [];
  error = "";
  searchByEmail = {} as SearchByEmailResponse;
  coInvestigator = {} as CoInvestigatorResponse;
  coInvestigatorEdit = {} as CoInvestigator;
  documentResponse = {} as DocumentResponse;
  documentsResponse = {} as DocumentsResponse;
  irbchairAppsResponse = {} as IrbchairAppsResponse;
  irbMembers = [];
  irbAppTitle = "";
  irbTransactionLog = [];
  irbTransactionLogs = [];
  irbchairApp = {} as IrbchairApp;
  irbMemberAssignedApps = {} as IrbchairAppsResponse;
  irbMemberStandardApps = {} as IrbchairAppsResponse;
  irbAppEdit = {} as Application;
  nonStandard = {} as NonStandardResponse;
  reviewer = {} as Reviewer;
  reviewNotes = {} as ReviewNoteResponse;
  reviewNotesType = {};
  reviewNoteEdit = {} as ReviewNote;
  votes = {} as VoteResponse;
  vote = {} as Vote;
  approvalLetters = [];
  notificationForms = [];
  dashboardInfo = {};
  notificationAlertSuscription = {};
  notificationAlerts = {};
  notificationAlertsPending = {};

  /**
   * Get current applications
   * @returns Array<Application>
   */
  get currentNotificationAlertSuscription() {
    return this.notificationAlertSuscription;
  }

  get currentIrbAppTitleFilter() {
    return this.irbAppTitle;
  }

  get currentNotificationAlerts() {
    return this.notificationAlerts;
  }

  get currentNotificationAlertsPending() {
    return this.notificationAlertsPending;
  }

  get currentApplications(): ApplicationResponse {
    return this.applications;
  }

  get getAppsInformationSearch(): ApplicationResponse {
    return this.appInformationSearch;
  }

  get currentSubmittedApplications() {
    return this.submittedApplications;
  }

  get currentDashboardInfo() {
    return this.dashboardInfo;
  }

  get currentCollaborativeApplications(): Array<Application> {
    return this.collaborativeApplications;
  }
  /**
   * Get current screening
   * @returns Screening
   */
  get currentScreening(): Screening {
    return this.screening;
  }

  get currentApplication(): ApplicationResponse {
    return this.application;
  }

  get currentFaculty(): FacultyResponse {
    return this.faculty;
  }

  get currentNonStandard(): NonStandardResponse {
    return this.nonStandard;
  }

  get currentResearch(): ResearchResponse {
    return this.research;
  }

  get currentSubject(): SubjectResponse {
    return this.subject;
  }

  get currentRecruitment(): RecruitmentResponse {
    return this.recruitment;
  }

  get currentRisk(): RiskResponse {
    return this.risk;
  }

  get currentInformed(): InformedResponse {
    return this.informed;
  }

  get currentEditorToken() {
    // return "eefrmb0ci1k8oh30yb46k6sz3csbg638pvr5845i55eqte91";
    return "no-api-key";
  }

  get currentData(): DataResponse {
    return this.data;
  }

  get getIrbError(): string {
    return this.error;
  }

  get currentCoInvestigators(): Array<CoInvestigator> {
    return this.coInvestigators;
  }

  get currentIrbMembers(): Array<IrbMember> {
    return this.irbMembers;
  }

  get currentIrbTransactionLogs(): Array<IrbMember> {
    return this.irbTransactionLogs;
  }

  get currentIrbTransactionLog(): Array<IrbMember> {
    return this.irbTransactionLog;
  }

  get currentSearchByEmail(): SearchByEmailResponse {
    return this.searchByEmail;
  }

  get currentCoInvestigator(): CoInvestigatorResponse {
    return this.coInvestigator;
  }

  get currentCoInvestigatorEdit(): CoInvestigator {
    return this.coInvestigatorEdit;
  }

  get currentIrbAppEdit(): Application {
    return this.irbAppEdit;
  }

  get currentDocumentResponse(): DocumentResponse {
    return this.documentResponse;
  }

  get currentDocumentsResponse(): DocumentsResponse {
    return this.documentsResponse;
  }

  get currentIrbchairApps(): IrbchairAppsResponse {
    return this.irbchairAppsResponse;
  }

  get currentIrbchairApp(): IrbchairApp {
    return this.irbchairApp;
  }

  get currentIrbMemberAssignedApps(): IrbchairAppsResponse {
    return this.irbMemberAssignedApps;
  }

  get currentIrbMemberStandardApps(): IrbchairAppsResponse {
    return this.irbMemberStandardApps;
  }

  get currentIrbReviewer(): Reviewer {
    return this.reviewer;
  }

  get currentIrbReviewNotes(): ReviewNoteResponse {
    return this.reviewNotes;
  }

  get currentIrbReviewNotesType() {
    return this.reviewNotesType;
  }

  get currentReviewNoteEdit(): ReviewNote {
    return this.reviewNoteEdit;
  }

  get currentVotes(): VoteResponse {
    return this.votes;
  }

  get currentVote(): Vote {
    return this.vote;
  }

  get currentApprovalLetters(): Array<ApprovalLetter> {
    return this.approvalLetters;
  }

  get currentNotificationForms(): Array<NotificationForm> {
    return this.notificationForms;
  }

  @Mutation
  [Mutations.SET_IRB_APPLICATIONS](applications) {
    this.applications = applications;
  }

  @Mutation
  [Mutations.SET_SUBMITTED_IRB_APPLICATIONS](submittedApplications) {
    this.submittedApplications = submittedApplications;
  }

  @Mutation
  [Mutations.SET_COLLABORATIVE_IRB_APPLICATIONS](collaborativeApplications) {
    this.collaborativeApplications = collaborativeApplications;
  }

  @Mutation
  [Mutations.SET_IRB_APPLICATION](application) {
    this.application = application;
  }

  @Mutation
  [Mutations.SET_APPS_INFORMATION](data) {
    this.appInformationSearch = data;
  }

  @Mutation
  [Mutations.SET_IRB_APPLICATION_FACULTY](faculty) {
    this.faculty = faculty;
  }

  @Mutation
  [Mutations.SET_IRB_NON_STANDARD](nonStandard) {
    this.nonStandard = nonStandard;
  }

  @Mutation
  [Mutations.SET_IRB_APPLICATION_RESEARCH](research) {
    this.research = research;
  }

  @Mutation
  [Mutations.SET_IRB_APPLICATION_SUBJECT](subject) {
    this.subject = subject;
  }

  @Mutation
  [Mutations.SET_IRB_APPLICATION_RECRUITMENT](recruitment) {
    this.recruitment = recruitment;
  }
  @Mutation
  [Mutations.SET_IRB_APPLICATION_RISK](risk) {
    this.risk = risk;
  }

  @Mutation
  [Mutations.SET_IRB_APPLICATION_INFORMED](informed) {
    this.informed = informed;
  }

  @Mutation
  [Mutations.SET_IRB_APPLICATION_DATA](data) {
    this.data = data;
  }

  @Mutation
  [Mutations.SET_IRB_SCREENING](screening) {
    this.screening = screening;
  }

  @Mutation
  [Mutations.SET_IRB_ERROR](error) {
    this.error = error;
  }

  @Mutation
  [Mutations.SET_CO_INVESTIGATORS](coInvestigators) {
    this.coInvestigators = coInvestigators;
  }

  @Mutation
  [Mutations.SET_SEARCH_BY_EMAIL](searchByEmail) {
    this.searchByEmail = searchByEmail;
  }

  @Mutation
  [Mutations.SET_IRB_COINVESTIGATOR](coInvestigator) {
    this.coInvestigator = coInvestigator;
  }

  @Mutation
  [Mutations.SET_IRB_COINVESTIGATOR_EDIT](coInvestigatorEdit) {
    this.coInvestigatorEdit = coInvestigatorEdit;
  }

  @Mutation
  [Mutations.SET_IRB_APP_EDIT](irbAppEdit) {
    this.irbAppEdit = irbAppEdit;
  }

  @Mutation
  [Mutations.SET_IRB_DOCUMENT_DATA](documentResponse) {
    this.documentResponse = documentResponse;
  }

  @Mutation
  [Mutations.SET_IRB_DOCUMENTS](documentsResponse) {
    this.documentsResponse = documentsResponse;
  }

  @Mutation
  [Mutations.SET_IRBCHAIR_APPS](irbchairAppsResponse) {
    this.irbchairAppsResponse = irbchairAppsResponse;
  }

  @Mutation
  [Mutations.SET_IRBMEMBER_ASSIGNED_APPS](irbMemberAssignedApps) {
    this.irbMemberAssignedApps = irbMemberAssignedApps;
  }

  @Mutation
  [Mutations.SET_IRBMEMBER_STANDARD_APPS](irbMemberStandardApps) {
    this.irbMemberStandardApps = irbMemberStandardApps;
  }

  @Mutation
  [Mutations.SET_IRBMEMBERS](irbMembers) {
    this.irbMembers = irbMembers;
  }

  @Mutation
  [Mutations.SET_IRBTRANSACTION_LOG](irbtransactionslogs) {
    this.irbTransactionLog = irbtransactionslogs;
  }

  @Mutation
  [Mutations.SET_IRBTRANSACTION_LOGS](irbtransactionslogs) {
    this.irbTransactionLogs = irbtransactionslogs;
  }

  @Mutation
  [Mutations.SET_IRBCHAIR_APP](irbchairApp) {
    this.irbchairApp = irbchairApp;
  }

  @Mutation
  [Mutations.SET_IRB_REVIEWER](reviewer) {
    this.reviewer = reviewer;
  }

  @Mutation
  [Mutations.SET_IRB_REVIEW_NOTE](note) {
    this.reviewNoteEdit = note;
  }

  @Mutation
  [Mutations.SET_IRB_REVIEW_NOTES](notes) {
    this.reviewNotes = notes;
  }

  @Mutation
  [Mutations.SET_IRB_REVIEW_NOTES_TYPE](notes) {
    this.reviewNotesType = notes;
  }

  @Mutation
  [Mutations.SET_IRB_STANDARD_VOTES](votes) {
    this.votes = votes;
  }

  @Mutation
  [Mutations.SET_IRB_STANDARD_VOTE](vote) {
    this.vote = vote;
  }

  @Mutation
  [Mutations.SET_IRB_APPROVAL_LETTERS](letters) {
    this.approvalLetters = letters;
  }

  @Mutation
  [Mutations.SET_IRB_DASHBOARD_APPLICATION](dashboardInfo) {
    this.dashboardInfo = dashboardInfo;
  }

  @Mutation
  [Mutations.SET_IRB_SUSCRIBE_NOTIFICATION_ALERT](
    notificationAlertSuscription,
  ) {
    this.notificationAlertSuscription = notificationAlertSuscription;
  }

  @Mutation
  [Mutations.SET_IRB_NOTIFICATION_ALERT](notificationAlerts) {
    this.notificationAlerts = notificationAlerts;
  }

  @Mutation
  [Mutations.SET_IRB_NOTIFICATION_ALERT_PENDING](notificationAlertsPending) {
    this.notificationAlertsPending = notificationAlertsPending;
  }

  @Mutation
  [Mutations.SET_IRB_APP_TITLE](title) {
    this.irbAppTitle = title;
  }

  @Action
  [Actions.GET_IRB_APPLICATION_LIST](params) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      if (params) {
        console.log("with param");
        ApiService.query("irb/paging", params)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRB_APPLICATIONS, data);
          })
          .catch(() => {
            this.context.commit(Mutations.SET_IRB_APPLICATIONS, {});
          });
      } else {
        console.log("without param");
        ApiService.get("irb")
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRB_APPLICATIONS, data);
          })
          .catch(() => {
            this.context.commit(Mutations.SET_IRB_APPLICATIONS, {});
          });
      }
    } else {
      this.context.commit(Mutations.SET_IRB_APPLICATIONS, {});
    }
  }

  @Action
  [Actions.GET_IRB_SUSCRIBE_NOTIFICATION_ALERT](params) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      if (params) {
        ApiService.getEventSource("irb/notificationAlert/subscribe", params, {
          onDownloadProgress: (progressEvent) => {
            this.context.commit(Mutations.SET_IRB_SUSCRIBE_NOTIFICATION_ALERT, {
              data: progressEvent?.event?.responseText,
              updated: true,
            });
            return;
          },
        })
          .then(({ data }) => {
            this.context.commit(
              Mutations.SET_IRB_SUSCRIBE_NOTIFICATION_ALERT,
              data,
            );
          })
          .catch(() => {
            this.context.commit(
              Mutations.SET_IRB_SUSCRIBE_NOTIFICATION_ALERT,
              {},
            );
          });
      }
    } else {
      this.context.commit(Mutations.SET_IRB_SUSCRIBE_NOTIFICATION_ALERT, {});
    }
  }

  @Action
  [Actions.GET_IRB_NOTIFICATION_ALERT](params) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      if (params) {
        ApiService.query("irb/notificationAlert/alerts/1", params)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRB_NOTIFICATION_ALERT, data);
          })
          .catch(() => {
            this.context.commit(Mutations.SET_IRB_NOTIFICATION_ALERT, {});
          });
      }
    } else {
      this.context.commit(Mutations.SET_IRB_NOTIFICATION_ALERT, {});
    }
  }

  @Action
  [Actions.GET_IRB_NOTIFICATION_ALERT_PENDING](params) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      if (params) {
        ApiService.query("irb/notificationAlert/alerts/pending/1", params)
          .then(({ data }) => {
            this.context.commit(
              Mutations.SET_IRB_NOTIFICATION_ALERT_PENDING,
              data,
            );
          })
          .catch(() => {
            this.context.commit(
              Mutations.SET_IRB_NOTIFICATION_ALERT_PENDING,
              {},
            );
          });
      }
    } else {
      this.context.commit(Mutations.SET_IRB_NOTIFICATION_ALERT_PENDING, {});
    }
  }

  @Action
  [Actions.GET_IRB_NOTIFICATION_ALERT_VISITED](params) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      if (params) {
        ApiService.get("irb/notificationAlert/alerts/visited", params)
          .then(({ data }) => {
            console.log(data);
          })
          .catch((e) => {
            console.log(e);
          });
      }
    }
  }

  @Action
  [Actions.GET_IRB_SUBMITTED_APPLICATION_LIST](params) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      ApiService.query("irb/submitted", params)
        .then(({ data }) => {
          this.context.commit(
            Mutations.SET_SUBMITTED_IRB_APPLICATIONS,
            data.data,
          );
        })
        .catch(() => {
          this.context.commit(Mutations.SET_SUBMITTED_IRB_APPLICATIONS, []);
        });
    } else {
      this.context.commit(Mutations.SET_SUBMITTED_IRB_APPLICATIONS, []);
    }
  }

  @Action
  [Actions.GET_IRB_DASHBOARD_INFORMATION](info) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      ApiService.query("irb/dashboard", { params: info.params })
        .then(({ data }) => {
          this.context.commit(
            Mutations.SET_IRB_DASHBOARD_APPLICATION,
            data.data,
          );
        })
        .catch(() => {
          this.context.commit(Mutations.SET_IRB_DASHBOARD_APPLICATION, []);
        });
    } else {
      this.context.commit(Mutations.SET_IRB_DASHBOARD_APPLICATION, []);
    }
  }

  @Action
  [Actions.GET_IRB_COLLABORATIVE_APPLICATION_LIST](params) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      ApiService.query("irb/collaborative", params)
        .then(({ data }) => {
          console.log("co-pi");
          console.log(data);
          this.context.commit(
            Mutations.SET_COLLABORATIVE_IRB_APPLICATIONS,
            data.data,
          );
        })
        .catch(() => {
          this.context.commit(Mutations.SET_COLLABORATIVE_IRB_APPLICATIONS, []);
        });
    } else {
      this.context.commit(Mutations.SET_COLLABORATIVE_IRB_APPLICATIONS, []);
    }
  }

  @Action
  [Actions.GET_IRB_SCREENING]() {
    return new Promise<void>((resolve, reject) => {
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.get("irb/getScreening")
          .then(({ data }) => {
            console.log(data);
            this.context.commit(Mutations.SET_IRB_SCREENING, data);
            resolve();
          })
          .catch(({ response }) => {
            console.log(response);
            this.context.commit(Mutations.SET_IRB_SCREENING, {});
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_SCREENING, {});
        reject();
      }
    });
  }

  @Action
  [Actions.POST_IRB_SCREENING](data) {
    return new Promise<void>((resolve, reject) => {
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.post("irb/postScreening", data)
          .then(({ data }) => {
            console.log(data);
            console.log("then block");
            resolve();
          })
          .catch(({ response }) => {
            console.log(response);
            console.log("catch block");
            this.context.commit(Mutations.SET_IRB_ERROR, response.data.message);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_ERROR, {});
        reject();
      }
    });
  }

  @Action
  [Actions.POST_IRB_APPLICATION](data) {
    return new Promise<void>((resolve, reject) => {
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.post("irb/create", data)
          .then(({ data }) => {
            console.log(data);
            this.context.commit(Mutations.SET_IRB_APPLICATION, data);
            resolve();
          })
          .catch(({ response }) => {
            this.context.commit(Mutations.SET_IRB_ERROR, response.data.message);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_ERROR, "");
        reject();
      }
    });
  }

  @Action
  [Actions.GET_IRB_APPLICATION](id) {
    if (JwtService.getToken() && id != null && id != "") {
      ApiService.setHeader();
      ApiService.get("irb", id)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_IRB_APPLICATION, data);
        })
        .catch(() => {
          this.context.commit(Mutations.SET_IRB_APPLICATION, {});
          router.push("404");
        });
    } else {
      this.context.commit(Mutations.SET_IRB_APPLICATION, {});
    }
  }

  @Action
  [Actions.POST_IRB_FACULTY](data) {
    return new Promise<void>((resolve, reject) => {
      this.context.commit(Mutations.SET_IRB_ERROR, "");
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.post("irb/faculty/save", data)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRB_APPLICATION_FACULTY, data);
            resolve();
          })
          .catch(({ response }) => {
            this.context.commit(Mutations.SET_IRB_ERROR, response.data.message);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_ERROR, "");
        reject();
      }
    });
  }

  @Action
  [Actions.POST_IRB_FACULTY_SEND](data) {
    return new Promise<void>((resolve, reject) => {
      this.context.commit(Mutations.SET_IRB_ERROR, "");
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.post("irb/faculty/send", data)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRB_APPLICATION_FACULTY, data);
            resolve();
          })
          .catch(({ response }) => {
            this.context.commit(Mutations.SET_IRB_ERROR, response.data.message);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_ERROR, "");
        reject();
      }
    });
  }

  @Action
  [Actions.GET_IRB_APPLICATION_FACULTY](id) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      ApiService.get("irb/faculty/app", id)
        .then(({ data }) => {
          console.log(data);
          this.context.commit(Mutations.SET_IRB_APPLICATION_FACULTY, data);
        })
        .catch(({ response }) => {
          console.log(response);
          this.context.commit(Mutations.SET_IRB_APPLICATION_FACULTY, {});
        });
    } else {
      this.context.commit(Mutations.SET_IRB_APPLICATION_FACULTY, {});
    }
  }

  @Action
  [Actions.POST_IRB_NONSTANDARD](data) {
    return new Promise<void>((resolve, reject) => {
      this.context.commit(Mutations.SET_IRB_ERROR, "");
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.post("irb/nonStandard", data)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRB_NON_STANDARD, data);
            resolve();
          })
          .catch(({ response }) => {
            this.context.commit(Mutations.SET_IRB_ERROR, response.data.message);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_ERROR, "");
        reject();
      }
    });
  }

  @Action
  [Actions.GET_IRB_NONSTANDARD](id) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      ApiService.get("irb/nonStandard", id)
        .then(({ data }) => {
          console.log(data);
          this.context.commit(Mutations.SET_IRB_NON_STANDARD, data);
        })
        .catch(({ response }) => {
          console.log(response);
          this.context.commit(Mutations.SET_IRB_NON_STANDARD, {});
        });
    } else {
      this.context.commit(Mutations.SET_IRB_NON_STANDARD, {});
    }
  }

  @Action
  [Actions.POST_IRB_RESEARCH](data) {
    return new Promise<void>((resolve, reject) => {
      this.context.commit(Mutations.SET_IRB_ERROR, "");
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.post("irb/research", data)
          .then(({ data }) => {
            console.log(data);
            this.context.commit(Mutations.SET_IRB_APPLICATION_RESEARCH, data);
            resolve();
          })
          .catch(({ response }) => {
            console.log(response);
            this.context.commit(Mutations.SET_IRB_ERROR, response.data.message);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_ERROR, "");
        reject();
      }
    });
  }

  @Action
  [Actions.GET_IRB_APPLICATION_RESEARCH](id) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      ApiService.get("irb/research", id)
        .then(({ data }) => {
          console.log(data);
          this.context.commit(Mutations.SET_IRB_APPLICATION_RESEARCH, data);
        })
        .catch(({ response }) => {
          console.log(response);
          this.context.commit(Mutations.SET_IRB_APPLICATION_RESEARCH, {});
        });
    } else {
      this.context.commit(Mutations.SET_IRB_APPLICATION_RESEARCH, {});
    }
  }

  @Action
  [Actions.POST_IRB_SUBJECT](data) {
    return new Promise<void>((resolve, reject) => {
      this.context.commit(Mutations.SET_IRB_ERROR, "");
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.post("irb/subject", data)
          .then(({ data }) => {
            console.log(data);
            this.context.commit(Mutations.SET_IRB_APPLICATION_SUBJECT, data);
            resolve();
          })
          .catch(({ response }) => {
            console.log(response);
            this.context.commit(Mutations.SET_IRB_ERROR, response.data.message);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_ERROR, "");
        reject();
      }
    });
  }

  @Action
  [Actions.GET_IRB_APPLICATION_SUBJECT](id) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      ApiService.get("irb/subject", id)
        .then(({ data }) => {
          console.log(data);
          this.context.commit(Mutations.SET_IRB_APPLICATION_SUBJECT, data);
        })
        .catch(({ response }) => {
          console.log(response);
          this.context.commit(Mutations.SET_IRB_APPLICATION_SUBJECT, {});
        });
    } else {
      this.context.commit(Mutations.SET_IRB_APPLICATION_SUBJECT, {});
    }
  }

  @Action
  [Actions.POST_IRB_RECRUITMENT](data) {
    return new Promise<void>((resolve, reject) => {
      this.context.commit(Mutations.SET_IRB_ERROR, "");
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.post("irb/recruitment", data)
          .then(({ data }) => {
            console.log(data);
            this.context.commit(
              Mutations.SET_IRB_APPLICATION_RECRUITMENT,
              data,
            );
            resolve();
          })
          .catch(({ response }) => {
            console.log(response);
            this.context.commit(Mutations.SET_IRB_ERROR, response.data.message);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_ERROR, "");
        reject();
      }
    });
  }

  @Action
  [Actions.GET_IRB_APPLICATION_RECRUITMENT](id) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      ApiService.get("irb/recruitment", id)
        .then(({ data }) => {
          console.log(data);
          this.context.commit(Mutations.SET_IRB_APPLICATION_RECRUITMENT, data);
        })
        .catch(({ response }) => {
          console.log(response);
          this.context.commit(Mutations.SET_IRB_APPLICATION_RECRUITMENT, {});
        });
    } else {
      this.context.commit(Mutations.SET_IRB_APPLICATION_RECRUITMENT, {});
    }
  }

  @Action
  [Actions.POST_IRB_RISK](data) {
    return new Promise<void>((resolve, reject) => {
      this.context.commit(Mutations.SET_IRB_ERROR, "");
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.post("irb/risk", data)
          .then(({ data }) => {
            console.log(data);
            this.context.commit(Mutations.SET_IRB_APPLICATION_RISK, data);
            resolve();
          })
          .catch(({ response }) => {
            console.log(response);
            this.context.commit(Mutations.SET_IRB_ERROR, response.data.message);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_ERROR, "");
        reject();
      }
    });
  }

  @Action
  [Actions.GET_IRB_APPLICATION_RISK](id) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      ApiService.get("irb/risk", id)
        .then(({ data }) => {
          console.log(data);
          this.context.commit(Mutations.SET_IRB_APPLICATION_RISK, data);
        })
        .catch(({ response }) => {
          console.log(response);
          this.context.commit(Mutations.SET_IRB_APPLICATION_RISK, {});
        });
    } else {
      this.context.commit(Mutations.SET_IRB_APPLICATION_RISK, {});
    }
  }

  @Action
  [Actions.POST_IRB_INFORMED](data) {
    return new Promise<void>((resolve, reject) => {
      this.context.commit(Mutations.SET_IRB_ERROR, "");
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.post("irb/informed", data)
          .then(({ data }) => {
            console.log(data);
            this.context.commit(Mutations.SET_IRB_APPLICATION_INFORMED, data);
            resolve();
          })
          .catch(({ response }) => {
            console.log(response);
            this.context.commit(Mutations.SET_IRB_ERROR, response.data.message);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_ERROR, "");
        reject();
      }
    });
  }

  @Action
  [Actions.GET_IRB_APPLICATION_INFORMED](id) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      ApiService.get("irb/informed", id)
        .then(({ data }) => {
          console.log(data);
          this.context.commit(Mutations.SET_IRB_APPLICATION_INFORMED, data);
        })
        .catch(({ response }) => {
          console.log(response);
          this.context.commit(Mutations.SET_IRB_APPLICATION_INFORMED, {});
        });
    } else {
      this.context.commit(Mutations.SET_IRB_APPLICATION_INFORMED, {});
    }
  }

  @Action
  [Actions.POST_IRB_DATA](data) {
    return new Promise<void>((resolve, reject) => {
      this.context.commit(Mutations.SET_IRB_ERROR, "");
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.post("irb/data", data)
          .then(({ data }) => {
            console.log(data);
            this.context.commit(Mutations.SET_IRB_APPLICATION_DATA, data);
            resolve();
          })
          .catch(({ response }) => {
            console.log(response);
            this.context.commit(Mutations.SET_IRB_ERROR, response.data.message);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_ERROR, "");
        reject();
      }
    });
  }

  @Action
  [Actions.GET_IRB_APPLICATION_DATA](id) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      ApiService.get("irb/data", id)
        .then(({ data }) => {
          console.log(data);
          this.context.commit(Mutations.SET_IRB_APPLICATION_DATA, data);
        })
        .catch(({ response }) => {
          console.log(response);
          this.context.commit(Mutations.SET_IRB_APPLICATION_DATA, {});
        });
    } else {
      this.context.commit(Mutations.SET_IRB_APPLICATION_DATA, {});
    }
  }

  @Action
  [Actions.GET_CO_INVESTIGATORS](id) {
    return new Promise<void>((resolve, reject) => {
      if (JwtService.getToken() && id != null && id != "") {
        ApiService.setHeader();
        ApiService.get("irb/coinvestigator/app", id)
          .then(({ data }) => {
            console.log(data);
            this.context.commit(Mutations.SET_CO_INVESTIGATORS, data.data);
            resolve();
          })
          .catch(({ response }) => {
            console.log(response);
            this.context.commit(Mutations.SET_CO_INVESTIGATORS, []);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_CO_INVESTIGATORS, []);
        reject();
      }
    });
  }

  @Action
  [Actions.POST_SEARCH_BY_EMAIL](data) {
    return new Promise<void>((resolve, reject) => {
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.post("irb/coinvestigator/searchByEmail", data)
          .then(({ data }) => {
            console.log(data);
            this.context.commit(Mutations.SET_SEARCH_BY_EMAIL, data);
            resolve();
          })
          .catch(({ response }) => {
            console.log(response);
            this.context.commit(Mutations.SET_SEARCH_BY_EMAIL, {});
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_SEARCH_BY_EMAIL, {});
        reject();
      }
    });
  }

  @Action
  [Actions.POST_IRB_COINVESTIGATOR](data) {
    return new Promise<void>((resolve, reject) => {
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.post("irb/coinvestigator", data)
          .then(({ data }) => {
            console.log(data);
            this.context.commit(Mutations.SET_IRB_COINVESTIGATOR, data);
            resolve();
          })
          .catch(({ response }) => {
            console.log(response);
            this.context.commit(Mutations.SET_IRB_ERROR, response.data.message);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_ERROR, "");
        reject();
      }
    });
  }

  @Action
  [Actions.DELETE_IRB_COINVESTIGATOR](id) {
    return new Promise<void>((resolve, reject) => {
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.delete(`irb/coinvestigator/${id}`)
          .then(({ data }) => {
            console.log(data);
            this.context.commit(Mutations.SET_IRB_ERROR, data.message);
            resolve();
          })
          .catch(({ response }) => {
            console.log(response);
            this.context.commit(Mutations.SET_IRB_ERROR, response.data.message);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_ERROR, "Something wrong");
        reject();
      }
    });
  }

  @Action
  [Actions.GET_IRB_DOCUMENTS](info) {
    return new Promise<void>((resolve, reject) => {
      const id =
        info.length > 1 &&
        info[1] != null &&
        info[1] != undefined &&
        info[1] != ""
          ? info[0] + "/" + info[1].value
          : info[0];

      if (JwtService.getToken()) {
        console.log("doc");
        ApiService.setHeader();
        ApiService.get("irb/documents/app", id)
          .then(({ data }) => {
            console.log(data);
            this.context.commit(Mutations.SET_IRB_DOCUMENTS, data);
            resolve();
          })
          .catch(({ response }) => {
            console.log(response);
            this.context.commit(Mutations.SET_IRB_DOCUMENTS, {});
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_DOCUMENTS, {});
        reject();
      }
    });
  }

  @Action
  [Actions.DELETE_IRB_DOCUMENTS](data) {
    return new Promise<void>((resolve, reject) => {
      if (JwtService.getToken()) {
        const url = "irb/documents/app/" + data.appId + "/doc/" + data.id;
        const finalUrl =
          data.classification != null &&
          (data.classification != null) != undefined &&
          data.classification != ""
            ? url + "/classification/" + data.classification
            : url;
        ApiService.setHeader();
        ApiService.delete(finalUrl)
          .then(({ data }) => {
            console.log(data);
            this.context.commit(Mutations.SET_IRB_DOCUMENTS, data);
            resolve();
          })
          .catch(({ response }) => {
            console.log(response);
            this.context.commit(Mutations.SET_IRB_DOCUMENTS, {});
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_DOCUMENTS, {});
        reject();
      }
    });
  }

  @Action
  [Actions.POST_IRB_DOCUMENT](data) {
    return new Promise<void>((resolve, reject) => {
      if (JwtService.getToken()) {
        ApiService.setHeader();
        debugger;
        ApiService.postConfig("irb/documents/", data, {
          headers: { "Content-Type": "multipart/form-data" },
        })
          .then(({ data }) => {
            console.log(data);
            this.context.commit(Mutations.SET_IRB_DOCUMENT_DATA, data);
            resolve();
          })
          .catch(({ response }) => {
            this.context.commit(Mutations.SET_IRB_ERROR, response.message);
            console.log(response);
            this.context.commit(Mutations.SET_IRB_ERROR, response.data.message);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_ERROR, "Something wrong");
        reject();
      }
    });
  }

  @Action
  [Actions.DELETE_IRB_APPLICATION](id) {
    return new Promise<void>((resolve, reject) => {
      if (JwtService.getToken() && id != null && id != "") {
        ApiService.setHeader();
        ApiService.delete(`irb/${id}`)
          .then(({ data }) => {
            console.log(data);
            this.context.commit(Mutations.SET_IRB_ERROR, data.message);
            resolve();
          })
          .catch(() => {
            this.context.commit(Mutations.SET_IRB_ERROR, "Something wrong");
            //console.log(data);
            //this.context.commit(Mutations.SET_IRB_ERROR, response.data.message);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_ERROR, "Something wrong");
        reject();
      }
    });
  }

  @Action
  [Actions.SUBMIT_IRB_APPLICATION](id) {
    return new Promise<void>((resolve, reject) => {
      if (JwtService.getToken() && id != null && id != "") {
        ApiService.setHeader();
        ApiService.put(`irb/submit/${id}`, id)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRB_ERROR, data.message);
            resolve();
          })
          .catch(({ response }) => {
            this.context.commit(Mutations.SET_IRB_ERROR, response.data.message);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_ERROR, "Something wrong");
        reject();
      }
    });
  }

  @Action
  [Actions.UPDATE_IRB_APPLICATION](data) {
    return new Promise<void>((resolve, reject) => {
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.post(`irb/update`, data)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRB_ERROR, data.message);
            resolve();
          })
          .catch(({ response }) => {
            this.context.commit(Mutations.SET_IRB_ERROR, response.data.message);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_ERROR, "Something wrong");
        reject();
      }
    });
  }

  @Action
  [Actions.GET_IRBCHAIR_APPS](params) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      if (params) {
        console.log("with param");
        console.log(params);
        ApiService.query("irbchair/applications", params)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRBCHAIR_APPS, data);
          })
          .catch(() => {
            this.context.commit(Mutations.SET_IRBCHAIR_APPS, {});
          });
      } else {
        console.log("without param");
        ApiService.get("irbchair/applications")
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRBCHAIR_APPS, data);
          })
          .catch(() => {
            this.context.commit(Mutations.SET_IRBCHAIR_APPS, {});
          });
      }
    } else {
      this.context.commit(Mutations.SET_IRBCHAIR_APPS, {});
    }
  }

  @Action
  [Actions.GET_IRBCHAIR_APPS_EXPEDITED](params) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      if (params) {
        console.log("with param");
        console.log(params);
        ApiService.query("irbchair/applications-expedited", params)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRBCHAIR_APPS, data);
          })
          .catch(() => {
            this.context.commit(Mutations.SET_IRBCHAIR_APPS, {});
          });
      } else {
        console.log("without param");
        ApiService.get("irbchair/applications-expedited")
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRBCHAIR_APPS, data);
          })
          .catch(() => {
            this.context.commit(Mutations.SET_IRBCHAIR_APPS, {});
          });
      }
    } else {
      this.context.commit(Mutations.SET_IRBCHAIR_APPS, {});
    }
  }

  @Action
  [Actions.GET_APPS_INFORMATION](params) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      if (params) {
        console.log("with param");
        console.log(params);
        ApiService.query("irb/appInformation", params)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_APPS_INFORMATION, data);
          })
          .catch(() => {
            this.context.commit(Mutations.SET_APPS_INFORMATION, {});
          });
      } else {
        console.log("without param");
        ApiService.get("irb/appInformation")
          .then(({ data }) => {
            this.context.commit(Mutations.SET_APPS_INFORMATION, data);
          })
          .catch(() => {
            this.context.commit(Mutations.SET_APPS_INFORMATION, {});
          });
      }
    } else {
      this.context.commit(Mutations.SET_APPS_INFORMATION, {});
    }
  }

  @Action
  [Actions.GET_IRBMEMBERS]() {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      ApiService.get("irbchair/members")
        .then(({ data }) => {
          this.context.commit(Mutations.SET_IRBMEMBERS, data);
        })
        .catch(() => {
          this.context.commit(Mutations.SET_IRBMEMBERS, {});
        });
    } else {
      this.context.commit(Mutations.SET_IRBMEMBERS, {});
    }
  }

  @Action
  [Actions.GET_IRBTRANSACTION_LOG](applicationId) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      ApiService.query("/irb/transactionLog/" + applicationId.id, {
        params: applicationId.params,
      })
        .then(({ data }) => {
          this.context.commit(Mutations.SET_IRBTRANSACTION_LOG, data);
        })
        .catch(() => {
          this.context.commit(Mutations.SET_IRBTRANSACTION_LOG, {});
        });
    } else {
      this.context.commit(Mutations.SET_IRBTRANSACTION_LOG, {});
    }
  }

  @Action
  [Actions.GET_IRBTRANSACTION_LOGS](data) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      ApiService.query("/irb/transactionLog/all/", { params: data.params })
        .then(({ data }) => {
          this.context.commit(Mutations.SET_IRBTRANSACTION_LOGS, data);
        })
        .catch(() => {
          this.context.commit(Mutations.SET_IRBTRANSACTION_LOGS, {});
        });
    } else {
      this.context.commit(Mutations.SET_IRBTRANSACTION_LOGS, {});
    }
  }

  @Action
  [Actions.POST_IRB_ASSIGN](data) {
    return new Promise<void>((resolve, reject) => {
      this.context.commit(Mutations.SET_IRB_ERROR, "");
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.post("irbchair/assign", data)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRB_ERROR, data.message);
            resolve();
          })
          .catch(({ response }) => {
            this.context.commit(Mutations.SET_IRB_ERROR, response.data.message);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_ERROR, "");
        reject();
      }
    });
  }

  @Action
  [Actions.GET_IRBMEMBER_ASSIGNED_APPS](params) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      if (params) {
        console.log("with param");
        console.log(params);
        ApiService.query("irbmember/assigned", params)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRBMEMBER_ASSIGNED_APPS, data);
          })
          .catch(() => {
            this.context.commit(Mutations.SET_IRBMEMBER_ASSIGNED_APPS, {});
          });
      } else {
        console.log("without param");
        ApiService.get("irbmember/assigned")
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRBMEMBER_ASSIGNED_APPS, data);
          })
          .catch(() => {
            this.context.commit(Mutations.SET_IRBMEMBER_ASSIGNED_APPS, {});
          });
      }
    } else {
      this.context.commit(Mutations.SET_IRBMEMBER_ASSIGNED_APPS, {});
    }
  }

  @Action
  [Actions.GET_IRBMEMBER_STANDARD_APPS](params) {
    if (JwtService.getToken()) {
      ApiService.setHeader();
      if (params) {
        console.log("with param");
        console.log(params);
        ApiService.query("irbmember/standard", params)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRBMEMBER_STANDARD_APPS, data);
          })
          .catch(() => {
            this.context.commit(Mutations.SET_IRBMEMBER_STANDARD_APPS, {});
          });
      } else {
        console.log("without param");
        ApiService.get("irbmember/standard")
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRBMEMBER_STANDARD_APPS, data);
          })
          .catch(() => {
            this.context.commit(Mutations.SET_IRBMEMBER_STANDARD_APPS, {});
          });
      }
    } else {
      this.context.commit(Mutations.SET_IRBMEMBER_STANDARD_APPS, {});
    }
  }

  @Action
  [Actions.GET_IRB_REVIEWER](id) {
    return new Promise<void>((resolve, reject) => {
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.get("irb/reviewer", id)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRB_REVIEWER, data.data);
            resolve();
          })
          .catch(() => {
            this.context.commit(Mutations.SET_IRB_REVIEWER, {});
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_REVIEWER, {});
        reject();
      }
    });
  }

  @Action
  [Actions.GET_IRB_REVIEW_NOTES](id) {
    return new Promise<void>((resolve, reject) => {
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.get("irb/reviewer/notes", id)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRB_REVIEW_NOTES, data);
            resolve();
          })
          .catch(() => {
            this.context.commit(Mutations.SET_IRB_REVIEW_NOTES, {});
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_REVIEW_NOTES, {});
        reject();
      }
    });
  }

  @Action
  [Actions.GET_IRB_REVIEW_NOTES_TYPE](id) {
    return new Promise<void>((resolve, reject) => {
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.query("irb/reviewer/allnotestype", id)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRB_REVIEW_NOTES_TYPE, data);
            resolve();
          })
          .catch(() => {
            this.context.commit(Mutations.SET_IRB_REVIEW_NOTES_TYPE, {});
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_REVIEW_NOTES_TYPE, {});
        reject();
      }
    });
  }

  @Action
  [Actions.GET_IRB_REVIEW_NOTE](param) {
    return new Promise<void>((resolve, reject) => {
      if (JwtService.getToken()) {
        ApiService.setHeader();
        console.log(param);
        ApiService.query("irb/reviewer/note", param)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRB_REVIEW_NOTE, data.data);
            resolve();
          })
          .catch(() => {
            this.context.commit(Mutations.SET_IRB_REVIEW_NOTE, null);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_REVIEW_NOTE, null);
        reject();
      }
    });
  }

  @Action
  [Actions.POST_IRB_REVIEW_NOTE](data) {
    return new Promise<void>((resolve, reject) => {
      this.context.commit(Mutations.SET_IRB_ERROR, "");
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.post("irb/reviewer/note", data)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRB_ERROR, data.message);
            resolve();
          })
          .catch(({ response }) => {
            this.context.commit(Mutations.SET_IRB_ERROR, response.data.message);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_ERROR, "");
        reject();
      }
    });
  }

  @Action
  [Actions.POST_IRB_MAKE_DECISION](data) {
    return new Promise<void>((resolve, reject) => {
      this.context.commit(Mutations.SET_IRB_ERROR, "");
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.post("irbmember/decision", data)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRB_ERROR, data.message);
            resolve();
          })
          .catch(({ response }) => {
            this.context.commit(Mutations.SET_IRB_ERROR, response.data.message);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_ERROR, "");
        reject();
      }
    });
  }

  @Action
  [Actions.POST_IRB_CHAIR_MAKE_DECISION](data) {
    return new Promise<void>((resolve, reject) => {
      this.context.commit(Mutations.SET_IRB_ERROR, "");
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.post("irbchair/decision", data)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRB_ERROR, data.message);
            resolve();
          })
          .catch(({ response }) => {
            this.context.commit(Mutations.SET_IRB_ERROR, response.data.message);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_ERROR, "");
        reject();
      }
    });
  }

  @Action
  [Actions.GET_IRB_STANDARD_VOTES](id) {
    return new Promise<void>((resolve, reject) => {
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.get("irbmember/standard/votes", id)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRB_STANDARD_VOTES, data.data);
            resolve();
          })
          .catch(() => {
            this.context.commit(Mutations.SET_IRB_STANDARD_VOTES, {});
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_STANDARD_VOTES, {});
        reject();
      }
    });
  }

  @Action
  [Actions.GET_IRB_STANDARD_VOTE](id) {
    return new Promise<void>((resolve, reject) => {
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.get("irbmember/standard/vote", id)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRB_STANDARD_VOTE, data.data);
            resolve();
          })
          .catch(() => {
            this.context.commit(Mutations.SET_IRB_STANDARD_VOTE, null);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_STANDARD_VOTE, null);
        reject();
      }
    });
  }

  @Action
  [Actions.POST_IRB_MAKE_VOTE](data) {
    return new Promise<void>((resolve, reject) => {
      this.context.commit(Mutations.SET_IRB_ERROR, "");
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.post("irbmember/standard/vote", data)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRB_ERROR, data.message);
            resolve();
          })
          .catch(({ response }) => {
            this.context.commit(Mutations.SET_IRB_ERROR, response.data.message);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_ERROR, "");
        reject();
      }
    });
  }

  @Action
  [Actions.GET_IRB_APPROVAL_LETTERS](params) {
    return new Promise<void>((resolve, reject) => {
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.query("irb/approvals", params)
          .then(({ data }) => {
            this.context.commit(Mutations.SET_IRB_APPROVAL_LETTERS, data.data);
            resolve();
          })
          .catch(() => {
            this.context.commit(Mutations.SET_IRB_APPROVAL_LETTERS, null);
            reject();
          });
      } else {
        this.context.commit(Mutations.SET_IRB_APPROVAL_LETTERS, null);
        reject();
      }
    });
  }

  @Action
  [Actions.GET_IRB_APPROVAL_LETTER_PDF](params) {
    return new Promise<void>((resolve, reject) => {
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.query("pdfcontroller/approvalletter2/" + params, {
          headers: {
            Accept: "application/pdf",
          },
          responseType: "blob",
        })
          .then(({ data }) => {
            const blob = new Blob([data], { type: "application/pdf" });
            const objectUrl = window.URL.createObjectURL(blob);
            window.open(objectUrl);
            //this.context.commit(Mutations.SET_IRB_APPROVAL_LETTERS, data.data);
            resolve();
          })
          .catch(() => {
            // this.context.commit(Mutations.SET_IRB_APPROVAL_LETTERS, null);
            reject();
          });
      } else {
        //this.context.commit(Mutations.SET_IRB_APPROVAL_LETTERS, null);
        reject();
      }
    });
  }

  @Action
  [Actions.GET_IRB_APP_PDF](params) {
    return new Promise<void>((resolve, reject) => {
      if (JwtService.getToken()) {
        ApiService.setHeader();
        ApiService.query("pdfcontroller/appInfo/" + params, {
          headers: {
            Accept: "application/pdf",
          },
          responseType: "blob",
        })
          .then(({ data }) => {
            //this.context.commit(Mutations.SET_IRB_APPROVAL_LETTERS, data.data);
            const blob = new Blob([data], { type: "application/pdf" });
            const objectUrl = window.URL.createObjectURL(blob);
            window.open(objectUrl);
            resolve();
          })
          .catch(() => {
            //this.context.commit(Mutations.SET_IRB_APPROVAL_LETTERS, null);
            reject();
          });
      } else {
        //this.context.commit(Mutations.SET_IRB_APPROVAL_LETTERS, null);
        reject();
      }
    });
  }
}
